/**
 * LuaRuntime — Wasmoon-based Lua 5.4 scripting engine.
 *
 * Single VM with per-entity environment tables. Sandboxed globals.
 *
 * Security model:
 *  - `load`, `loadfile`, `dofile`, `require`, `os`, `io`, `package` are
 *    removed from the global scope after init.
 *  - The original `load` is saved on the JS side and temporarily injected
 *    only during host-initiated compilation (`loadScript`). This prevents
 *    entity scripts from compiling arbitrary bytecode at runtime.
 *  - Per-entity environments shadow `_G` with themselves so scripts cannot
 *    reach the real global table via `_G.load` or metatable walks.
 *  - Script source and arguments are passed through wasmoon's host API
 *    (`engine.global.set`) rather than string interpolation, preventing
 *    Lua code injection via specially-crafted strings.
 */

import { LuaFactory, type LuaEngine } from 'wasmoon';
import { Logger } from '../core/Logger.js';

/** Only alphanumeric + underscore, must start with letter or underscore. */
const HOOK_NAME_RE = /^[a-zA-Z_]\w*$/;
const GLOBAL_NAME_RE = /^[A-Za-z_][A-Za-z0-9_]*$/;

function isPlainFacade(v: object): boolean {
  const proto = Object.getPrototypeOf(v);
  return (proto === Object.prototype || proto === null) && !Array.isArray(v);
}

/** Own enumerable accessor (getter) keys of a facade object. */
function accessorKeys(v: object): string[] {
  const out: string[] = [];
  for (const k of Object.keys(v)) {
    const d = Object.getOwnPropertyDescriptor(v, k);
    if (d && typeof d.get === 'function') out.push(k);
  }
  return out;
}

/**
 * Copy a bridge facade for the Lua boundary so every function's `null`
 * return becomes `undefined`. WHY: with injectObjects:false wasmoon has no
 * null type extension — returning `null` from a bridge RAISES a Lua error
 * ("Cannot read properties of null") instead of yielding nil, so every
 * documented "not found → nil" path (physics.raycast miss, xr.controllerPose
 * with the controller down, nav.findPath unreachable, ~60 sites) struck the
 * calling script's error counter. Rewriting the boundary once here keeps the
 * JS-facing facades (and their tests) unchanged. Nested plain sub-tables
 * (`physics.vehicle.*`, `ai.orca.*`) are wrapped recursively; `skip` keys
 * (live getters) are left to __mklive.
 */
function wrapFacadeForLua(obj: object, skip: string[] | null, depth: number): object {
  if (depth > 4) return obj;
  const out: Record<string, unknown> = {};
  const src = obj as Record<string, unknown>;
  for (const k of Object.keys(src)) {
    if (skip && skip.includes(k)) continue;
    const v = src[k];
    if (typeof v === 'function') {
      const fn = v as (...a: unknown[]) => unknown;
      out[k] = function (this: unknown, ...a: unknown[]): unknown {
        const r = fn.apply(this, a);
        return r === null ? undefined : r;
      };
    } else if (v !== null && typeof v === 'object' && isPlainFacade(v)) {
      out[k] = wrapFacadeForLua(v, null, depth + 1);
    } else {
      out[k] = v;
    }
  }
  return out;
}

export class LuaRuntime {
  private engine: LuaEngine | null = null;
  // Per-instance LuaFactory — each runtime owns its own WASM module instance.
  // Sharing a module-level factory across runtimes is fragile in tests, hot-
  // reload, and any multi-world scenario: closing one engine can corrupt
  // another's WASM linear memory because both came from the same factory.
  private factory: LuaFactory | null = null;
  private scriptEnvs = new Map<number, string>(); // entityId → env table name
  private scriptErrors = new Map<number, number>(); // entityId → error count
  // entityId → monotonically increasing load generation. Bumped every time a
  // script is (re)loaded for an id. Lets deferred consumers (e.g. the destroy
  // system) detect that an id was recycled to a NEW entity/script before their
  // deferred pass ran, so they don't tear down the wrong entity's script.
  private scriptGenerations = new Map<number, number>();
  private nextGeneration = 1;
  // entityId → last successfully-loaded source. Kept so a failed hot-reload can
  // roll back to the previously-working script instead of leaving the entity
  // scriptless. Only the most recent good source is retained.
  private scriptSources = new Map<number, string>();
  // Cached JS-callable for the Lua-side __callHook dispatcher (see
  // applySandbox). Fetched once per engine; reset whenever the engine is
  // (re)created. Replaces the old per-call chunk interpolation + compile.
  private hookDispatcher: ((...args: unknown[]) => unknown) | null = null;
  private static readonly MAX_ERRORS = 3;

  async init(): Promise<void> {
    if (!this.factory) {
      this.factory = new LuaFactory();
    }
    this.engine = await this.factory.createEngine({
      openStandardLibs: true,
      injectObjects: false,
      enableProxy: false,
      traceAllocations: false,
    });

    // Apply sandbox restrictions. This also installs the Lua-side __mkenv +
    // __loadinto builders (capturing native setmetatable/load as upvalues)
    // before those globals are removed — entity envs and compilation must
    // happen Lua-side (see applySandbox for the wasmoon bridge rationale).
    this.applySandbox();
    this.hookDispatcher = null; // re-fetch from the fresh engine on first call

    Logger.info('Lua', 'Runtime initialized');
  }

  /**
   * Register engine API modules as Lua globals.
   */
  registerGlobal(name: string, value: unknown): void {
    if (!this.engine) return;
    if (value !== null && typeof value === 'object' && isPlainFacade(value)) {
      const getters = accessorKeys(value);
      if (
        getters.length > 0 && GLOBAL_NAME_RE.test(name)
        && typeof this.engine.global.get('__mklive') === 'function'
      ) {
        // WHY: wasmoon pushes a JS object as a plain Lua table by reading
        // every enumerable property ONCE at global.set() — accessor
        // properties (the `time` bridge's `get dt()`) were evaluated at
        // registration and frozen, so `time.dt`/`elapsed`/`frame` read 0
        // forever. Hand the sandbox a host getter (hidden by the "__"
        // prefix) plus the static members; __mklive builds a table whose
        // __index calls back into the host on every read.
        const host = value as Record<string, unknown>;
        this.engine.global.set(`__liveget_${name}`, (k: unknown) => {
          const v = host[String(k)];
          return v === null ? undefined : v;
        });
        this.engine.global.set(`__livebase_${name}`, wrapFacadeForLua(value, getters, 0));
        this.engine.doStringSync(`__mklive("${name}")`);
        return;
      }
      this.engine.global.set(name, wrapFacadeForLua(value, null, 0));
      return;
    }
    this.engine.global.set(name, value);
  }

  /**
   * Load a script for an entity. The script should return a table of
   * lifecycle hooks.
   *
   * Source and filename are passed through the wasmoon host API
   * (engine.global.set) rather than string interpolation, which prevents
   * Lua code injection via `]==]` sequences or other special characters.
   */
  async loadScript(entityId: number, source: string, fileName: string): Promise<boolean> {
    if (!this.engine) return false;
    if (!Number.isInteger(entityId) || entityId < 0) {
      Logger.warn('Lua', `Invalid entityId for loadScript: ${entityId}`);
      return false;
    }

    const envName = `_env_${entityId}`;
    try {
      // Create the isolated environment table for this entity via the
      // Lua-side builder (see applySandbox). entityId is a validated
      // non-negative integer, safe to interpolate as a number.
      this.engine.doStringSync(`__mkenv("${envName}", ${entityId})`);

      // Pass source and chunk name safely via host API — no string
      // interpolation of user content into Lua code.
      this.engine.global.set('__hsrc', source);
      this.engine.global.set('__hname', '@' + fileName);

      // Compile + run the chunk Lua-side (see __loadinto in applySandbox).
      // Compiling Lua-side is required: a JS-held `load` does not bind the
      // 4th _ENV argument, which would leave globals (and every bridge)
      // unreachable from hooks. Temps are cleaned up in the same sync call.
      // WHY a single return value: wasmoon's doStringSync hands back ONLY the
      // first value of a Lua multi-return (never an array), so the old
      // `return ok, err` form threw the parser's message away and every
      // compile error surfaced as a bare "script load failed".
      const result = this.engine.doStringSync(`
        local ok, err = __loadinto("${envName}", __hsrc, __hname)
        __hsrc = nil
        __hname = nil
        if ok then return true end
        return tostring(err)
      `);
      if (result !== true) {
        throw new Error(typeof result === 'string' ? result : 'script load failed');
      }

      this.scriptEnvs.set(entityId, envName);
      this.scriptErrors.set(entityId, 0);
      // Stamp a fresh load generation so a deferred destroy queued against a
      // PRIOR occupant of this id is invalidated (the id was recycled to a new
      // script). See getLoadGeneration + LuaDestroySystem.
      this.scriptGenerations.set(entityId, this.nextGeneration++);
      // Retain the working source so reload() can roll back on a failed compile.
      this.scriptSources.set(entityId, source);
      return true;
    } catch (err) {
      // Clean up temporaries and orphaned env table on error
      try {
        this.engine.doStringSync(`__hsrc = nil; __hname = nil; ${envName} = nil`);
      } catch { /* ignore cleanup errors */ }
      Logger.error('Lua', `Failed to load script for entity ${entityId}: ${err}`);
      return false;
    }
  }

  /**
   * Call a lifecycle hook on an entity's script.
   *
   * hookName is validated against an alphanumeric pattern to prevent
   * injection. Arguments are passed through the wasmoon host API
   * (engine.global.set) rather than string interpolation.
   */
  callHook(entityId: number, hookName: string, ...args: unknown[]): unknown {
    if (!this.engine) return undefined;

    // Validate hook name — only alphanumeric + underscore, must start
    // with a letter or underscore. Prevents Lua injection via hookName.
    if (!HOOK_NAME_RE.test(hookName)) {
      Logger.warn('Lua', `Invalid hook name rejected: ${hookName}`);
      return undefined;
    }

    const envName = this.scriptEnvs.get(entityId);
    if (!envName) return undefined;

    // Check if entity has exceeded error limit
    const errors = this.scriptErrors.get(entityId) ?? 0;
    if (errors >= LuaRuntime.MAX_ERRORS) return undefined;

    try {
      // Call the persistent Lua-side dispatcher (installed in applySandbox)
      // instead of compiling a fresh interpolated chunk per invocation — the
      // old path paid a full Lua parse+compile per scripted entity per 60 Hz
      // tick. Args pass positionally through wasmoon's function bridge
      // (numbers/strings/booleans marshal natively; undefined → nil).
      //
      // Return shape (unchanged): wasmoon returns ONLY the FIRST value of a
      // Lua multi-return, so the dispatcher returns a SINGLE sentinel-keyed
      // table marshalled to a JS object: `{ __hookErr = msg }` on failure,
      // `{ value = r }` on success. The two keys are disjoint, so a hook
      // legitimately returning a table (even one with a `value` field) is
      // never misclassified as an error.
      if (!this.hookDispatcher) {
        const fn = this.engine.global.get('__callHook') as unknown;
        if (typeof fn !== 'function') return undefined; // sandbox not applied
        this.hookDispatcher = fn as (...a: unknown[]) => unknown;
      }
      // WHY null → undefined (the old `a ?? null` was exactly backwards): with
      // injectObjects:false wasmoon marshals `undefined` to nil but THROWS on
      // `null` ("Cannot read properties of null (reading 'then')") before the
      // Lua function is entered. `msg.send(id, "ping")` with no payload and
      // any event whose payload sanitises to null struck the script's error
      // counter three times and disabled it. `args` is our own rest array,
      // so scrubbing it in place costs no allocation.
      for (let i = 0; i < args.length; i++) if (args[i] === null) args[i] = undefined;
      const result = this.hookDispatcher(envName, hookName, ...args);

      // Detect a caught hook error via the sentinel key. wasmoon marshals the
      // returned table to a plain JS object, so __hookErr is a string iff the
      // hook raised.
      if (result && typeof result === 'object' && !Array.isArray(result)) {
        const wrapped = result as { __hookErr?: unknown; value?: unknown };
        if (typeof wrapped.__hookErr === 'string') {
          const luaErr = wrapped.__hookErr;
          const newCount = errors + 1;
          this.scriptErrors.set(entityId, newCount);
          Logger.error('Lua', `Hook ${hookName} error on entity ${entityId} (${newCount}/${LuaRuntime.MAX_ERRORS}): ${luaErr}`);
          if (newCount >= LuaRuntime.MAX_ERRORS) {
            Logger.warn('Lua', `Entity ${entityId} script disabled after ${LuaRuntime.MAX_ERRORS} errors`);
          }
          return undefined;
        }
        // Success path: unwrap the hook's actual return value.
        return wrapped.value;
      }

      return undefined;
    } catch (err) {
      const newCount = errors + 1;
      this.scriptErrors.set(entityId, newCount);
      Logger.error('Lua', `Hook ${hookName} error on entity ${entityId} (${newCount}/${LuaRuntime.MAX_ERRORS}): ${err}`);
      if (newCount >= LuaRuntime.MAX_ERRORS) {
        Logger.warn('Lua', `Entity ${entityId} script disabled after ${LuaRuntime.MAX_ERRORS} errors`);
      }
      return undefined;
    }
  }

  /**
   * Check if an entity's script is still active (not disabled by errors).
   */
  isScriptActive(entityId: number): boolean {
    const errors = this.scriptErrors.get(entityId) ?? 0;
    return errors < LuaRuntime.MAX_ERRORS && this.scriptEnvs.has(entityId);
  }

  /**
   * Unload a script for an entity.
   */
  unloadScript(entityId: number): void {
    const envName = this.scriptEnvs.get(entityId);
    if (envName && this.engine) {
      try {
        this.engine.doStringSync(`${envName} = nil`);
      } catch { /* ignore cleanup errors */ }
    }
    this.scriptEnvs.delete(entityId);
    this.scriptErrors.delete(entityId);
    this.scriptGenerations.delete(entityId);
    this.scriptSources.delete(entityId);
  }

  /**
   * Current load generation for an entity id, or 0 if no script is loaded.
   *
   * Increments on every successful `loadScript` for that id. Deferred consumers
   * capture this at queue time and re-check at flush time: if it changed (or
   * dropped to 0), the id was unloaded or recycled to a different script and the
   * deferred action must be skipped to avoid acting on the wrong entity.
   */
  getLoadGeneration(entityId: number): number {
    return this.scriptGenerations.get(entityId) ?? 0;
  }

  /**
   * Reset error count for an entity (e.g., after hot-reload).
   */
  resetErrors(entityId: number): void {
    this.scriptErrors.set(entityId, 0);
  }

  // ─── Hot-reload support ──────────────────────────────────────────────────────

  /**
   * Hot-reload a script for an entity.
   *
   * NOTE on closure survival: this does NOT re-execute the new source inside the
   * existing env. It unloads (wipes the env table) and rebuilds from scratch, so
   * upvalues/closures and any non-scalar state do NOT survive. Only scalar
   * globals are preserved, and only by the caller (ScriptReloader) which
   * snapshots them via {@link readEnvScalars} before reload and restores them via
   * {@link writeEnvScalars} after. The error counter is reset so a previously-
   * broken script can recover.
   *
   * Rollback: if the new source fails to compile/run, the previously-working
   * source (captured at last successful load) is reloaded so a bad hot-reload
   * does not leave the entity scriptless. Returns true only if the NEW source
   * loaded; false if it failed (regardless of whether rollback succeeded).
   *
   * Security guarantees are unchanged — loadScript passes source via the host
   * API, never string interpolation.
   */
  async reload(entityId: number, newSource: string, fileName: string): Promise<boolean> {
    // Snapshot the currently-working source BEFORE unload wipes it, so a failed
    // reload can be rolled back to the last good script.
    const previousSource = this.scriptSources.get(entityId);

    // Unload existing script (clears env table)
    this.unloadScript(entityId);

    // Reset error counter so a previously-broken script can recover
    this.resetErrors(entityId);

    // Re-load with new source
    const ok = await this.loadScript(entityId, newSource, fileName);
    if (ok) return true;

    // New source failed. Roll back to the last working script (if any) so the
    // entity keeps running rather than going dark on a bad hot-reload.
    if (previousSource !== undefined) {
      const restored = await this.loadScript(entityId, previousSource, fileName);
      if (restored) {
        Logger.warn('Lua', `Reload of entity ${entityId} failed; rolled back to previous script`);
      } else {
        Logger.error('Lua', `Reload of entity ${entityId} failed AND rollback failed; entity is scriptless`);
      }
    }
    return false;
  }

  /**
   * Read simple scalar globals from an entity's Lua environment.
   *
   * Returns a JSON-safe snapshot of number/string/boolean values currently
   * set in the entity's environment table. Tables, functions, and userdata
   * are intentionally skipped — they are not serializable.
   *
   * Used by ScriptReloader to preserve state across a hot-reload.
   */
  readEnvScalars(entityId: number): Record<string, string | number | boolean | null> {
    if (!this.engine) return {};

    const envName = this.scriptEnvs.get(entityId);
    if (!envName) return {};

    const result: Record<string, string | number | boolean | null> = {};

    try {
      // Ask Lua to serialise scalars from the entity env into a JS-accessible table
      // We iterate the env and return only primitive values.
      // The result is returned as a Lua table which wasmoon converts to a JS object.
      const raw = this.engine.doStringSync(`
        local out = {}
        for k, v in pairs(${envName}) do
          local t = type(v)
          if (t == "number" or t == "string" or t == "boolean") and type(k) == "string" then
            -- Skip private/internal keys
            if k:sub(1,1) ~= "_" then
              out[k] = v
            end
          end
        end
        return out
      `);

      if (raw && typeof raw === 'object') {
        for (const [k, v] of Object.entries(raw as Record<string, unknown>)) {
          if (typeof v === 'number' || typeof v === 'string' || typeof v === 'boolean') {
            result[k] = v;
          }
        }
      }
    } catch (err) {
      Logger.warn('Lua', `readEnvScalars failed for entity ${entityId}: ${err}`);
    }

    return result;
  }

  /**
   * Write simple scalar globals back into an entity's Lua environment.
   *
   * Used by ScriptReloader to restore state after a hot-reload.
   * Only number/string/boolean values are written; others are silently ignored.
   */
  writeEnvScalars(entityId: number, state: Record<string, string | number | boolean | null>): void {
    if (!this.engine) return;

    const envName = this.scriptEnvs.get(entityId);
    if (!envName) return;

    for (const [key, value] of Object.entries(state)) {
      if (value === null) continue;
      if (typeof value !== 'number' && typeof value !== 'string' && typeof value !== 'boolean') {
        continue;
      }
      // Validate key to prevent injection
      if (!HOOK_NAME_RE.test(key)) continue;

      try {
        this.engine.global.set('__hrval', value);
        this.engine.doStringSync(`${envName}.${key} = __hrval; __hrval = nil`);
      } catch (err) {
        Logger.warn('Lua', `writeEnvScalars key "${key}" failed for entity ${entityId}: ${err}`);
      }
    }
  }

  /**
   * Sandbox the Lua environment by removing dangerous globals.
   *
   * Called after `load` has been saved on the JS side. Removes `load`,
   * `loadfile`, `dofile`, `require`, `os`, `io`, `package`, raw*
   * functions, `collectgarbage`, and `string.dump` (bytecode dumping).
   * Keeps only `debug.traceback` from the debug library. Replaces
   * `math.random` with a deterministic seedable xorshift32 PRNG.
   *
   * Per-entity environment builder (`__mkenv`):
   *  Before `setmetatable` is removed, a closure is installed that captures
   *  the native `setmetatable` and the real `_G` as upvalues. `loadScript`
   *  calls it to build each entity's environment table natively.
   *
   *  This MUST be done Lua-side: wasmoon's JS↔Lua bridge does NOT correctly
   *  bind a metatable when `setmetatable` is round-tripped through JS (saved
   *  to a JS var and re-injected). The table argument crosses the bridge as a
   *  copy, so the metatable lands on the wrong object and `__index` never
   *  fires — leaving every registered bridge global (`time`, `input`, …,
   *  `events`, `msg`) UNREACHABLE from entity hooks. The previous saved-
   *  `setmetatable` approach silently exhibited this; literal returns worked
   *  but any global access inside a hook resolved to nil.
   *
   *  Security: the env's `__index` is a function that hides every `__`-prefixed
   *  key (the builder itself, plus all `__h*`/`__ca*` host temporaries) and
   *  otherwise falls through to the real `_G` — which has already had `load`,
   *  `setmetatable`, `os`, `io`, etc. removed below. Scripts therefore cannot
   *  reach `__mkenv`, `setmetatable`, or `load` to escape the sandbox.
   */
  private applySandbox(): void {
    if (!this.engine) return;
    this.engine.doStringSync(`
      -- Per-entity environment builder + compiler. Capture the native
      -- setmetatable, load, and real _G as upvalues BEFORE they are removed
      -- below. Both env construction (setmetatable) and script compilation
      -- (load with a 4th _ENV arg) MUST happen Lua-side: wasmoon's JS↔Lua
      -- bridge (enableProxy:false) does NOT bind a table argument that was
      -- passed to a JS-held copy of these C functions, so the metatable /
      -- _ENV lands on the wrong object and global lookups inside hooks
      -- resolve to nil.
      do
        local _smt = setmetatable
        local _realG = _G
        local _load = load
        local _error = error
        -- Read-only proxy factory for stdlib tables. Per-entity sandbox
        -- isolation hole: env __index falls through to _realG, whose
        -- string/math/table are SHARED MUTABLE tables, so a write like
        -- math.random = evil in one entity poisoned every other entity
        -- (verified). Each env is
        -- instead given a read-only proxy view of each stdlib lib: reads/calls
        -- pass through to the native lib, but writes raise an error and never
        -- reach the shared table. The proxy itself is immutable, so a single
        -- proxy per lib can be safely shared across all envs.
        --   * __index = lib  → all functions remain reachable & callable.
        --   * __newindex     → blocks (and surfaces) any attempt to mutate.
        --   * __metatable    → hides the metatable from getmetatable.
        -- setmetatable/rawset are removed from script reach below, so scripts
        -- cannot strip or bypass this guard.
        local function mkReadonly(lib)
          return _smt({}, {
            __index = lib,
            __newindex = function(_t, k, _v)
              _error("cannot modify read-only stdlib table (key: " .. tostring(k) .. ")", 2)
            end,
            __metatable = false,
          })
        end
        local _ro_string, _ro_math, _ro_table
        -- __index resolver: hide host internals from scripts and fall through
        -- to the sandboxed real _G for everything else. Hidden:
        --   * "__"-prefixed keys  → builder closures + host temporaries
        --   * "_env_"-prefixed     → other entities' environment tables
        --     (prevents one script reaching another's globals via _G)
        -- Engine bridge globals (events, input, audio, …) are SHARED host
        -- objects. Scripts get a read-only view that also hides the host-only
        -- members: "__"-prefixed controls (events.__pump / __clearEntity) and
        -- "dispose" (every bridge exposes it for BridgeRegistry.disposeAll --
        -- a script calling city.dispose() tore the live generator down). The
        -- __newindex guard stops one entity forging another's input.isPressed.
        local _bridgeProxies = _smt({}, { __mode = "k" })
        local function mkBridgeProxy(lib)
          local p = _bridgeProxies[lib]
          if p then return p end
          p = _smt({}, {
            __index = function(_t, k)
              if type(k) == "string" and (k:sub(1, 2) == "__" or k == "dispose") then return nil end
              return lib[k]
            end,
            __newindex = function(_t, k, _v)
              _error("cannot modify engine bridge table (key: " .. tostring(k) .. ")", 2)
            end,
            __metatable = false,
          })
          _bridgeProxies[lib] = p
          return p
        end
        local function envIndex(_t, k)
          if type(k) == "string" then
            if k:sub(1, 2) == "__" then return nil end
            if k:sub(1, 5) == "_env_" then return nil end
          end
          local v = _realG[k]
          local tv = type(v)
          if tv == "table" or tv == "userdata" then return mkBridgeProxy(v) end
          return v
        end
        -- Live accessor globals (see LuaRuntime.registerGlobal): a bridge
        -- whose members are JS getters is exposed as a table whose __index
        -- calls the hidden host getter on EVERY read instead of the frozen
        -- snapshot wasmoon takes when pushing a plain object.
        __mklive = function(name)
          local getter = _realG["__liveget_" .. name]
          local base = _realG["__livebase_" .. name] or {}
          _realG["__livebase_" .. name] = nil
          _realG[name] = _smt(base, {
            __index = function(_t, k) return getter(k) end,
            __metatable = false,
          })
        end
        __mkenv = function(name, id)
          local env = _smt({}, { __index = envIndex })
          env._ENV = env
          env._G = env
          env.self = { entity = id }
          -- Per-env read-only views of the stdlib so one script cannot poison
          -- another's string/math/table via the shared globals (see mkReadonly).
          -- Proxies are built lazily on first env creation: by then the
          -- seedable math.random replacement (installed at the end of
          -- applySandbox) is already in place, and __index is a live reference
          -- so the proxy reflects it.
          if not _ro_string then
            _ro_string = mkReadonly(string)
            _ro_math = mkReadonly(math)
            _ro_table = mkReadonly(table)
          end
          env.string = _ro_string
          env.math = _ro_math
          env.table = _ro_table
          _realG[name] = env
        end
        -- Compile 'src' with the entity env named 'name' as its _ENV, run the
        -- chunk, and store the returned hooks table on env._hooks. Returns
        -- (true, nil) on success or (false, errMessage) on failure. The env
        -- must already exist (created by __mkenv).
        __loadinto = function(name, src, chunk)
          local env = _realG[name]
          if not env then return false, "missing env: " .. tostring(name) end
          local fn, err = _load(src, chunk, "t", env)
          if not fn then return false, err end
          local ok, result = pcall(fn)
          if not ok then return false, result end
          if type(result) == "table" then env._hooks = result end
          return true, nil
        end
      end

      -- Remove dangerous globals
      os = nil
      io = nil
      load = nil
      loadfile = nil
      dofile = nil
      require = nil
      package = nil
      rawget = nil
      rawset = nil
      rawequal = nil
      rawlen = nil
      collectgarbage = nil
      getmetatable = nil
      setmetatable = nil
      coroutine = nil

      -- Prevent bytecode dumping
      string.dump = nil

      -- Keep debug.traceback but remove everything else. Also stash it under a
      -- private "__"-prefixed global that callHook uses for xpcall. This is
      -- necessary because "debug" is ALSO a registered bridge name — a script's
      -- DebugBridge global (debug.log/draw/...) overwrites _G.debug, which would
      -- otherwise leave xpcall(fn, debug.traceback) with a nil handler. The
      -- "__" prefix keeps __traceback hidden from scripts via the env __index.
      local traceback = debug.traceback
      debug = { traceback = traceback }
      __traceback = traceback

      -- Persistent hook dispatcher. callHook() used to interpolate + compile a
      -- FRESH Lua chunk per invocation — a full parse/compile per scripted
      -- entity per 60 Hz tick. This one function (fetched once JS-side and
      -- called directly) replaces that. Returns the same sentinel table shape
      -- the JS side already understands: {__hookErr=msg} | {value=result}.
      -- The "__" prefix hides it from entity scripts via the env __index.
      function __callHook(envName, hookName, ...)
        local env = _G[envName]
        if not env then return { value = nil } end
        local hooks = env._hooks
        if not (hooks and hooks[hookName]) then return { value = nil } end
        local ok, result = xpcall(hooks[hookName], __traceback, ...)
        if not ok then return { __hookErr = tostring(result) } end
        return { value = result }
      end

      -- Replace math.random with seedable PRNG (xorshift32)
      do
        local state = 12345
        function math.randomseed(seed)
          state = seed & 0xFFFFFFFF
          if state == 0 then state = 1 end
        end
        function math.random(m, n)
          state = (state ~ (state << 13)) & 0xFFFFFFFF
          state = (state ~ (state >> 17)) & 0xFFFFFFFF
          state = (state ~ (state << 5)) & 0xFFFFFFFF
          local r = (state % 1000000) / 1000000
          if m == nil then return r
          elseif n == nil then return math.floor(r * m) + 1
          else return math.floor(r * (n - m + 1)) + m
          end
        end
      end
    `);
  }

  dispose(): void {
    // Snapshot keys to avoid Map mutation during iteration
    const entityIds = Array.from(this.scriptEnvs.keys());
    for (const entityId of entityIds) {
      this.unloadScript(entityId);
    }
    // Close the engine
    if (this.engine) {
      this.engine.global.close();
      this.engine = null;
    }
    this.hookDispatcher = null; // belongs to the closed engine
    this.factory = null;
    Logger.info('Lua', 'Runtime disposed');
  }
}
